import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useEffect } from 'react';
import { useSEO } from '../../utils/seo';

const AboutPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://danvanremovals.co.uk';
  
  useSEO({
    title: 'About Us | Professional Removals Company | Dan & Van Lancashire',
    description: 'Learn about Dan & Van Removals. Local, trusted removals company serving Lancashire. Fully insured, professional team, 500+ moves completed. Call 07530 166035',
    keywords: 'about Dan Van Removals, Lancashire removals company, professional movers, trusted removals, local moving company',
    canonical: '/about',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Dan & Van Removals',
      description: 'Dan & Van Removals was founded on a simple principle: moving house or office doesn\'t have to be stressful. Based in Lancashire, we serve homeowners and businesses across the region.',
      mainEntity: {
        '@type': 'MovingCompany',
        name: 'Dan & Van Removals',
        telephone: '07530166035',
        areaServed: 'Lancashire, UK',
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Reliability',
      description: 'We turn up on time, every time, and deliver on our promises',
    },
    {
      icon: 'ri-heart-line',
      title: 'Care',
      description: 'Your belongings are treated with the utmost respect and attention',
    },
    {
      icon: 'ri-user-smile-line',
      title: 'Professionalism',
      description: 'Friendly, courteous service from experienced moving specialists',
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Transparency',
      description: 'Clear pricing with no hidden fees or surprise charges',
    },
  ];

  const stats = [
    { number: '500+', label: 'Moves Completed' },
    { number: '5.0/5', label: 'Customer Rating' },
    { number: '15+', label: 'Areas Covered' },
    { number: '100%', label: 'Fully Insured' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col lg:flex-row items-center mt-20">
        <div className="w-full lg:w-1/2 h-[40vh] sm:h-[50vh] lg:h-screen">
          <div
            className="w-full h-full bg-cover bg-center"
            style={{
              backgroundImage: `url('https://static.readdy.ai/image/ab22404d73908d3c144c597ec26057d7/dc70c129f63c53a94e508f2f77b251d9.jpeg')`,
            }}
          ></div>
        </div>

        <div className="w-full lg:w-1/2 bg-white px-6 lg:px-16 py-12 sm:py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-gold text-xs uppercase tracking-wider mb-4">About Us</div>
            <h1 className="font-sans text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-charcoal mb-6 sm:mb-8 leading-tight">
              Moving with care across Lancashire
            </h1>
            <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-gray-700 leading-relaxed">
              <p>
                Dan & Van Removals was founded on a simple principle: moving house or office doesn't have to be stressful. With years of experience in the removals industry, we've built our reputation on reliability, care, and professionalism.
              </p>
              <p>
                Based in Lancashire, we serve homeowners and businesses across the region, providing everything from complete house removals to single item transportation. Our team understands that every move is unique, which is why we tailor our service to meet your specific needs.
              </p>
              <p>
                We're fully insured, use professional-grade equipment, and treat every item as if it were our own. When you choose Dan & Van, you're choosing a local, trusted partner who genuinely cares about making your move as smooth as possible.
              </p>
            </div>

            <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-gold/20">
              <h3 className="font-sans text-lg sm:text-xl font-bold text-charcoal mb-4">Our Values</h3>
              <ul className="space-y-3">
                <li className="flex items-center text-sm sm:text-base text-gray-700">
                  <i className="ri-checkbox-circle-fill text-gold mr-3 flex-shrink-0"></i>
                  Professional, courteous service
                </li>
                <li className="flex items-center text-sm sm:text-base text-gray-700">
                  <i className="ri-checkbox-circle-fill text-gold mr-3 flex-shrink-0"></i>
                  Careful handling of all belongings
                </li>
                <li className="flex items-center text-sm sm:text-base text-gray-700">
                  <i className="ri-checkbox-circle-fill text-gold mr-3 flex-shrink-0"></i>
                  Transparent, competitive pricing
                </li>
                <li className="flex items-center text-sm sm:text-base text-gray-700">
                  <i className="ri-checkbox-circle-fill text-gold mr-3 flex-shrink-0"></i>
                  Fully insured for peace of mind
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-charcoal mb-2">
                  {stat.number}
                </div>
                <div className="text-sm sm:text-base text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12 sm:mb-16">
            <div className="text-gold text-xs uppercase tracking-wider mb-3">What Drives Us</div>
            <h2 className="font-sans text-3xl sm:text-4xl md:text-5xl font-bold text-charcoal">
              Our Core Values
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4 sm:mb-6 bg-gold/10 rounded-full">
                  <i className={`${value.icon} text-3xl sm:text-4xl text-gold`}></i>
                </div>
                <h3 className="font-sans text-lg sm:text-xl font-bold text-charcoal mb-3">
                  {value.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 sm:py-20 md:py-24 bg-charcoal text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div>
              <h2 className="font-sans text-3xl sm:text-4xl md:text-5xl font-bold mb-6 sm:mb-8">
                Why choose Dan & Van?
              </h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gold rounded-full flex items-center justify-center mr-4">
                    <i className="ri-map-pin-line text-lg sm:text-xl text-charcoal"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold mb-2">Local Knowledge</h3>
                    <p className="text-sm sm:text-base text-gray-300">
                      We know Lancashire inside out, from narrow village lanes to busy town centres
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gold rounded-full flex items-center justify-center mr-4">
                    <i className="ri-tools-line text-lg sm:text-xl text-charcoal"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold mb-2">Professional Equipment</h3>
                    <p className="text-sm sm:text-base text-gray-300">
                      Modern vehicles and specialist moving equipment for safe transportation
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gold rounded-full flex items-center justify-center mr-4">
                    <i className="ri-team-line text-lg sm:text-xl text-charcoal"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold mb-2">Experienced Team</h3>
                    <p className="text-sm sm:text-base text-gray-300">
                      Trained professionals who handle your belongings with care and respect
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gold rounded-full flex items-center justify-center mr-4">
                    <i className="ri-shield-check-line text-lg sm:text-xl text-charcoal"></i>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold mb-2">Fully Insured</h3>
                    <p className="text-sm sm:text-base text-gray-300">
                      Comprehensive insurance coverage for complete peace of mind
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-lg p-6 sm:p-8">
              <h3 className="font-serif text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Get in Touch</h3>
              <p className="text-sm sm:text-base text-gray-300 mb-6">
                Ready to experience our premium removals service? Contact us today for a free, no-obligation quote.
              </p>
              <div className="space-y-3 sm:space-y-4">
                <a
                  href="tel:07530166035"
                  className="block w-full py-3 sm:py-4 text-sm sm:text-base text-center border-2 border-gold text-gold font-semibold rounded hover:bg-gold hover:text-charcoal transition-all whitespace-nowrap cursor-pointer"
                >
                  Call 07530 166035
                </a>
                <Link
                  to="/contact"
                  className="block w-full py-3 sm:py-4 text-sm sm:text-base text-center bg-gold text-charcoal font-semibold rounded hover:shadow-luxury-hover transition-all whitespace-nowrap cursor-pointer"
                >
                  Get a Quote
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
